import { PERMISSION, type KnownPermissionKey } from './permissions';

export const ROLE = {
  /**
   * O titular do sistema: tem tudo, inclusive o que ainda vai existir, e **só uma pessoa**
   * o tem — o banco garante (`0042`). Só ele cuida de papéis e permissões, e só ele o passa
   * adiante (`TransferSuperAdmin`).
   */
  SUPER_ADMIN: 'SUPER_ADMIN',
  ADMIN: 'ADMIN',
  PLANEJAMENTO: 'PLANEJAMENTO',
  GESTOR_OBRA: 'GESTOR_OBRA',
  CONFERENTE: 'CONFERENTE',
  SUPRIMENTOS: 'SUPRIMENTOS',
  VISUALIZACAO: 'VISUALIZACAO',
} as const;

export type RoleKey = (typeof ROLE)[keyof typeof ROLE];

export interface RoleDefinition {
  readonly key: RoleKey;
  readonly name: string;
  readonly description: string;
  readonly permissions: readonly KnownPermissionKey[];
}

/**
 * As telas que todo papel de fábrica abre — as mesmas que o menu mostrava a quem tinha as
 * leituras abaixo, antes de a tela virar permissão própria. Ninguém perdeu tela na troca.
 */
const VIEW_PAGES: readonly KnownPermissionKey[] = [
  PERMISSION.PAGE_DASHBOARD,
  PERMISSION.PAGE_PROJECTS,
  PERMISSION.PAGE_STRUCTURE,
  PERMISSION.PAGE_TAKEOFF,
  PERMISSION.PAGE_SCHEDULE,
  PERMISSION.PAGE_SUPPLIES,
  PERMISSION.PAGE_WORKFORCE,
  PERMISSION.PAGE_WEEKLY_PLAN,
  PERMISSION.PAGE_DAILY_PLAN,
  // Só leitura, e o que ela mostra é o que a conferência e o RDO já mostram: todo papel de
  // fábrica a abre, como abre as outras telas de consulta.
  PERMISSION.PAGE_QUALITY,
  PERMISSION.PAGE_DISCIPLINES,
  PERMISSION.PAGE_SERVICES,
  PERMISSION.PAGE_CHECKLISTS,
  PERMISSION.PAGE_MATERIALS,
  PERMISSION.PAGE_CALENDAR,
  // Todas as abas das telas acima (RN-626): o papel de fábrica vê a tela inteira, e quem
  // quiser esconder uma aba a desmarca no papel.
  PERMISSION.TAB_TAKEOFF_ASSEMBLY,
  PERMISSION.TAB_TAKEOFF_ITEMS,
  PERMISSION.TAB_TAKEOFF_BUDGET,
  PERMISSION.TAB_SCHEDULE_LABOR,
  PERMISSION.TAB_SCHEDULE_MATERIALS,
  PERMISSION.TAB_SCHEDULE_ROOMS,
  PERMISSION.TAB_WORKFORCE_EMPLOYEES,
  PERMISSION.TAB_WORKFORCE_TEAMS,
  PERMISSION.TAB_WORKFORCE_OUTSOURCED,
  PERMISSION.TAB_WORKFORCE_ROLES,
  PERMISSION.TAB_SERVICES_CATALOG,
  PERMISSION.TAB_SERVICES_AVERAGES,
];

const VIEW_ONLY: readonly KnownPermissionKey[] = [
  PERMISSION.PROJECTS_VIEW,
  PERMISSION.STRUCTURE_VIEW,
  PERMISSION.TAKEOFF_VIEW,
  PERMISSION.SCHEDULE_VIEW,
  PERMISSION.MATERIALS_VIEW,
  PERMISSION.REQUIREMENTS_VIEW,
  PERMISSION.RESTRICTIONS_VIEW,
  PERMISSION.EMPLOYEES_VIEW,
  PERMISSION.REPORTS_VIEW,
  ...VIEW_PAGES,
];

/**
 * Matriz papel × permissão — o **padrão de fábrica**.
 *
 * Papéis são atalhos de atribuição; a decisão de autorização é sempre por permissão
 * granular (RN-133, ADR-017). Nenhum caso de uso pergunta "é ADMIN?" — pergunta
 * "tem `labor.plan`?".
 *
 * Desde que o papel virou editável, esta matriz vale enquanto ninguém mexeu nele: a
 * semeadura a reescreve só nos papéis sem `customizedAt`, e "restaurar o padrão" na tela
 * volta para ela. O Super administrador e o Administrador são a exceção — levam sempre o
 * que o código diz, com o que tiver entrado nesta publicação.
 */
export const ROLE_CATALOG: readonly RoleDefinition[] = [
  {
    key: ROLE.SUPER_ADMIN,
    name: 'Super administrador',
    description:
      'Titular do sistema: acesso completo, inclusive aos papéis e às permissões. Há um só, e ele o transfere.',
    permissions: Object.values(PERMISSION),
  },
  {
    key: ROLE.ADMIN,
    name: 'Administrador',
    description:
      'Cuida das pessoas e da obra: perfis, senhas, papéis (menos o de Super administrador) e backup.',
    // Tudo, menos mexer nos papéis: quem decide o que cada papel abre é o titular. O que
    // o Administrador faz com os papéis é **atribuí-los** às pessoas (`users.manage`).
    permissions: Object.values(PERMISSION).filter((key) => key !== PERMISSION.ROLES_MANAGE),
  },
  {
    key: ROLE.PLANEJAMENTO,
    name: 'Planejamento',
    description: 'Importa cronograma, planeja mão de obra e acompanha riscos.',
    permissions: [
      ...VIEW_ONLY,
      PERMISSION.STRUCTURE_MANAGE,
      PERMISSION.TAKEOFF_MANAGE,
      PERMISSION.SCHEDULE_IMPORT,
      PERMISSION.SCHEDULE_EDIT,
      PERMISSION.MATERIALS_MANAGE,
      PERMISSION.RESTRICTIONS_MANAGE,
      PERMISSION.LABOR_PLAN,
      PERMISSION.AUDIT_VIEW,
      PERMISSION.PAGE_AUDIT,
    ],
  },
  {
    key: ROLE.GESTOR_OBRA,
    name: 'Gestor da obra',
    description:
      'Planeja, confirma e altera a execução, inclusive alocação acima da capacidade com justificativa.',
    permissions: [
      ...VIEW_ONLY,
      PERMISSION.STRUCTURE_MANAGE,
      PERMISSION.TAKEOFF_MANAGE,
      PERMISSION.SCHEDULE_EDIT,
      PERMISSION.RESTRICTIONS_MANAGE,
      PERMISSION.EMPLOYEES_MANAGE,
      PERMISSION.LABOR_PLAN,
      PERMISSION.LABOR_ALLOCATE_OVERCAPACITY,
      PERMISSION.LABOR_CONFIRM,
      PERMISSION.LABOR_MODIFY,
      PERMISSION.AUDIT_VIEW,
      PERMISSION.PAGE_INSPECTION,
      // Quem confere abre a NC na medição e a encerra na aba (Fase 37).
      PERMISSION.PAGE_NON_CONFORMITIES,
      PERMISSION.NON_CONFORMITIES_MANAGE,
      PERMISSION.PAGE_AUDIT,
    ],
  },
  {
    key: ROLE.CONFERENTE,
    name: 'Conferente',
    description: 'Confere a execução diária, ajusta atividades e horas com justificativa.',
    permissions: [
      ...VIEW_ONLY,
      PERMISSION.LABOR_CONFIRM,
      PERMISSION.LABOR_MODIFY,
      PERMISSION.PAGE_INSPECTION,
      PERMISSION.PAGE_NON_CONFORMITIES,
      PERMISSION.NON_CONFORMITIES_MANAGE,
    ],
  },
  {
    key: ROLE.SUPRIMENTOS,
    name: 'Suprimentos',
    description: 'Acompanha necessidades de material e informa disponibilidade.',
    permissions: [...VIEW_ONLY, PERMISSION.REQUIREMENTS_STATUS_CHANGE, PERMISSION.MATERIALS_MANAGE],
  },
  {
    key: ROLE.VISUALIZACAO,
    name: 'Visualização',
    description: 'Somente leitura.',
    permissions: VIEW_ONLY,
  },
];
